import { ArrowLeftOutlined, PrinterOutlined, ScanOutlined, SmileOutlined } from '@ant-design/icons';
import type { DeliveryItemRow, SatisfactionRow, SignDeliveryRequest } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Rate,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hq } from '../api/client';
import { SlaBadge } from '../components/SlaBadge';
import { sumWidth, ellipsisCell, StickyActionBar, TipText } from '@hq/ui-kit';
import { printDocument } from '../print';

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待发货', color: 'default' },
  PICKING: { text: '拣货中', color: 'processing' },
  PACKED: { text: '已打包', color: 'processing' },
  SHIPPED: { text: '已发货', color: 'blue' },
  DELIVERING: { text: '配送中', color: 'blue' },
  ARRIVED: { text: '已到达', color: 'cyan' },
  SIGNED: { text: '已签收', color: 'green' },
  PART_SIGNED: { text: '部分签收', color: 'orange' },
  REJECTED: { text: '已拒收', color: 'red' },
  LOST: { text: '丢失', color: 'red' },
  CANCELED: { text: '已取消', color: 'default' }
};

const SIGNED_STATUSES = new Set(['SIGNED', 'PART_SIGNED', 'REJECTED', 'CANCELED', 'LOST']);

interface Draft {
  signQty: number;
  rejectQty: number;
  reason: string;
}

interface EvalForm {
  scoreTotal: number;
  tags?: string[];
  content?: string;
  anonFlag?: boolean;
}

/** 配送服务评价常用标签（可自由输入）。 */
const EVAL_TAGS = [
  { value: '准时送达', label: '准时送达' },
  { value: '包装完好', label: '包装完好' },
  { value: '数量准确', label: '数量准确' },
  { value: '态度好', label: '态度好' },
  { value: '配送慢', label: '配送慢' },
  { value: '包装破损', label: '包装破损' }
];

export default function DeliveryDetail() {
  const { id } = useParams<{ id: string }>();
  const deliveryId = id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const [open, setOpen] = useState(false);
  const [scanCode, setScanCode] = useState('');
  const [remark, setRemark] = useState('');
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [evalOpen, setEvalOpen] = useState(false);
  const [evalForm] = Form.useForm<EvalForm>();

  const query = useQuery({
    queryKey: ['delivery', 'detail', deliveryId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.delivery.detail(deliveryId),
    enabled: Boolean(deliveryId)
  });

  const sign = useMutation({
    mutationFn: (body: SignDeliveryRequest) => hq.delivery.sign(deliveryId, body),
    onSuccess: async (result) => {
      message.success(`签收成功：${result.signNo}`);
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['delivery'] });
      await query.refetch();
    }
  });

  const deliveryRow = query.data?.delivery;

  // 该配送单是否已有评价（同一业务单号只展示一次，避免重复入口）
  const existing = useQuery({
    queryKey: ['satisfaction', 'delivery', deliveryRow?.bizNo],
    placeholderData: keepPreviousData,
    queryFn: () => hq.satisfaction.list({ bizType: 'DELIVERY', bizNo: deliveryRow?.bizNo, size: 1 }),
    enabled: Boolean(deliveryRow?.bizNo)
  });

  const evaluate = useMutation({
    mutationFn: (values: EvalForm) =>
      hq.satisfaction.create({
        evalType: 'DELIVERY',
        bizType: 'DELIVERY',
        bizNo: deliveryRow?.bizNo,
        deptId: deliveryRow?.toDeptId,
        deptName: deliveryRow?.toDeptName ?? undefined,
        scoreTotal: values.scoreTotal,
        tags: values.tags && values.tags.length > 0 ? values.tags.join(',') : undefined,
        content: values.content?.trim() || undefined,
        anonFlag: values.anonFlag ?? false
      }),
    onSuccess: async (result) => {
      if (result.duplicated) message.info('该配送单已评价过，已展示原评价');
      else message.success('评价已提交，感谢反馈');
      setEvalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['satisfaction'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '评价提交失败')
  });

  /**
   * 后端是「累加后校验」：本次签收量叠加到已有 signQty 上，累计不得超 planQty。
   * 因此默认值与上限都必须是「剩余应收」，否则 PART_SIGNED 补签必然 42200。
   */
  const remainOf = (item: DeliveryItemRow): number => Math.max(0, item.planQty - item.signQty);

  const openSign = () => {
    const items = query.data?.items ?? [];
    const initial: Record<string, Draft> = {};
    for (const item of items) {
      initial[item.id] = { signQty: remainOf(item), rejectQty: 0, reason: '' };
    }
    setDrafts(initial);
    setScanCode('');
    setRemark('');
    setOpen(true);
  };

  const submitSign = () => {
    const items = query.data?.items ?? [];
    const payload = items.map((item) => {
      const draft = drafts[item.id];
      return {
        deliveryItemId: item.id,
        signQty: draft?.signQty ?? remainOf(item),
        rejectQty: draft?.rejectQty ?? 0,
        exceptionReason: draft?.reason || undefined
      };
    });
    sign.mutate({
      items: payload,
      scanCode: scanCode || undefined,
      remark: remark || undefined,
      exceptionDesc: payload.some((item) => item.rejectQty > 0) ? '部分拒收' : undefined
    });
  };

  if (query.isLoading) {
    return (
      <Card className="page-card">
        <Spin />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card className="page-card">
        <Alert
          type="error"
          showIcon
          message="配送单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
        <Button style={{ marginTop: 16 }} onClick={() => navigate('/delivery')}>
          返回列表
        </Button>
      </Card>
    );
  }

  const { delivery, items } = query.data;
  const meta = STATUS_META[delivery.deliveryStatus] ?? { text: delivery.deliveryStatus, color: 'default' };
  const canSign = !SIGNED_STATUSES.has(delivery.deliveryStatus);
  // 仅签收完成（含部分签收）后可评价配送服务
  const canEvaluate = delivery.deliveryStatus === 'SIGNED' || delivery.deliveryStatus === 'PART_SIGNED';
  const myEval: SatisfactionRow | undefined = existing.data?.items[0];

  const itemColumns: ColumnsType<DeliveryItemRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 140, render: (value: string | null) => value || '-' },
    { title: '应收', dataIndex: 'planQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '实收', dataIndex: 'signQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '拒收',
      dataIndex: 'rejectQty',
      width: 100,
      align: 'right',
      render: (value: number) => (value > 0 ? <Typography.Text type="danger">{formatQty(value)}</Typography.Text> : '-')
    },
    { title: '单价', dataIndex: 'price', width: 120, align: 'right', render: (value: number) => formatAmount(value, 4) },
    { title: '金额', dataIndex: 'amount', width: 130, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const draftColumns: ColumnsType<DeliveryItemRow> = [
    {
      title: '物资',
      dataIndex: 'materialName',
      ellipsis: { showTitle: false },
      render: (value: string | null, row) => <TipText text={value || row.materialCode || '-'} />
    },
    { title: '应收', dataIndex: 'planQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '实收',
      key: 'signQty',
      width: 110,
      render: (_value: unknown, row) => (
        <InputNumber
          min={0}
          max={remainOf(row)}
          value={drafts[row.id]?.signQty ?? remainOf(row)}
          onChange={(value) =>
            setDrafts((prev) => ({
              ...prev,
              [row.id]: {
                signQty: Number(value ?? 0),
                rejectQty: prev[row.id]?.rejectQty ?? 0,
                reason: prev[row.id]?.reason ?? ''
              }
            }))
          }
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: '拒收',
      key: 'rejectQty',
      width: 110,
      render: (_value: unknown, row) => (
        <InputNumber
          min={0}
          max={remainOf(row)}
          value={drafts[row.id]?.rejectQty ?? 0}
          onChange={(value) =>
            setDrafts((prev) => ({
              ...prev,
              [row.id]: {
                signQty: prev[row.id]?.signQty ?? remainOf(row),
                rejectQty: Number(value ?? 0),
                reason: prev[row.id]?.reason ?? ''
              }
            }))
          }
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: '异常原因',
      key: 'reason',
      width: 180,
      render: (_value: unknown, row) => (
        <Input
          placeholder="破损/缺货等"
          value={drafts[row.id]?.reason ?? ''}
          onChange={(event) =>
            setDrafts((prev) => ({
              ...prev,
              [row.id]: {
                signQty: prev[row.id]?.signQty ?? row.planQty,
                rejectQty: prev[row.id]?.rejectQty ?? 0,
                reason: event.target.value
              }
            }))
          }
        />
      )
    }
  ];

  return (
    <>
      <Card
        className="page-card"
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/delivery')} />
            <span>{delivery.bizNo}</span>
            <Tag color={meta.color}>{meta.text}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<PrinterOutlined />}
              onClick={() =>
                printDocument({
                  title: '配送签收单',
                  subtitle: delivery.bizNo,
                  meta: [
                    ['收货科室', delivery.toDeptName || '-'],
                    ['配送地址', delivery.toSpaceDesc || '-'],
                    ['配送人', delivery.deliverName || '-'],
                    ['车辆', delivery.vehicleNo || '-'],
                    ['计划送达', formatDisplayTime(delivery.planTime)],
                    ['签收时间', formatDisplayTime(delivery.signAt)]
                  ],
                  columns: [
                    { title: '行' },
                    { title: '物资编码' },
                    { title: '物资名称' },
                    { title: '规格' },
                    { title: '单位' },
                    { title: '计划', align: 'right' },
                    { title: '签收', align: 'right' },
                    { title: '拒收', align: 'right' }
                  ],
                  rows: items.map((item, index) => [
                    index + 1,
                    item.materialCode ?? '',
                    item.materialName ?? '',
                    item.spec ?? '',
                    item.unitName ?? '',
                    formatQty(item.planQty),
                    formatQty(item.signQty),
                    formatQty(item.rejectQty)
                  ]),
                  footer: '签收人签字：____________________　　日期：____________'
                })
              }
            >
              打印
            </Button>
            {canEvaluate && !existing.isLoading && !myEval ? (
              <Button icon={<SmileOutlined />} onClick={() => setEvalOpen(true)}>
                评价配送服务
              </Button>
            ) : null}
          </Space>
        }
      >
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="收货科室">{delivery.toDeptName || '-'}</Descriptions.Item>
          <Descriptions.Item label="配送地址">{delivery.toSpaceDesc || '-'}</Descriptions.Item>
          <Descriptions.Item label="配送人">{delivery.deliverName || '-'}</Descriptions.Item>
          <Descriptions.Item label="时效">
            <SlaBadge
              deadline={delivery.planTime}
              finished={['SIGNED', 'REJECTED', 'CANCELED', 'LOST'].includes(delivery.deliveryStatus)}
              finishedText={delivery.deliveryStatus === 'SIGNED' ? '已送达' : undefined}
            />
          </Descriptions.Item>
          <Descriptions.Item label="签收时间">{formatDisplayTime(delivery.signAt)}</Descriptions.Item>
          <Descriptions.Item label="车辆">{delivery.vehicleNo || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card className="page-card" title="配送明细">
        {items.length === 0 ? (
          <Empty description="无明细" />
        ) : (
          <Table<DeliveryItemRow> rowKey="id" size="middle" columns={itemColumns} dataSource={items} pagination={false} scroll={{ x: sumWidth(itemColumns) }} />
        )}
      </Card>

      {canSign ? (
        <StickyActionBar>
          <Button type="primary" icon={<ScanOutlined />} onClick={openSign}>
            扫码签收
          </Button>
          <Typography.Text type="secondary">向下逐项填写签收/拒收数量后提交</Typography.Text>
        </StickyActionBar>
      ) : null}

      {canEvaluate ? (
        <Card className="page-card" title="服务评价">
          {existing.isLoading ? (
            <Spin />
          ) : myEval ? (
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="我的评分">
                <Space size={6}>
                  <Rate disabled allowHalf value={Number(myEval.scoreTotal)} style={{ fontSize: 14 }} />
                  <Typography.Text type="secondary">{Number(myEval.scoreTotal).toFixed(1)}</Typography.Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="评价时间">{formatDisplayTime(myEval.evalAt)}</Descriptions.Item>
              <Descriptions.Item label="标签">{myEval.tags || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">{myEval.status === 'REPLIED' ? '客服已回复' : '待客服回复'}</Descriptions.Item>
              <Descriptions.Item label="评价内容" span={2}>
                {myEval.content || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="客服回复" span={2}>
                {myEval.replyContent
                  ? `${myEval.replyContent}（${formatDisplayTime(myEval.replyAt)}）`
                  : '暂无回复'}
              </Descriptions.Item>
            </Descriptions>
          ) : existing.isError ? (
            <Alert
              type="warning"
              showIcon
              message="评价信息加载失败"
              description={existing.error instanceof ApiError ? existing.error.friendlyMessage : String(existing.error)}
            />
          ) : (
            <Empty description="尚未评价本次配送服务，可点击右上角「评价配送服务」">
              <Button type="primary" icon={<SmileOutlined />} onClick={() => setEvalOpen(true)}>
                去评价
              </Button>
            </Empty>
          )}
        </Card>
      ) : null}

      <Modal
        title="扫码签收"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submitSign}
        okText="确认签收"
        confirmLoading={sign.isPending}
        width={900}
        destroyOnHidden
      >
        {sign.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="签收失败"
            description={sign.error instanceof ApiError ? sign.error.friendlyMessage : String(sign.error)}
          />
        ) : null}

        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap>
            <Input
              prefix={<ScanOutlined />}
              placeholder="扫描配送单码（可选）"
              value={scanCode}
              onChange={(event) => setScanCode(event.target.value)}
              style={{ width: 320 }}
            />
            <Input
              placeholder="签收备注（可选）"
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              style={{ width: 320 }}
            />
          </Space>
          <Table<DeliveryItemRow>
            rowKey="id"
            size="small"
            columns={draftColumns}
            dataSource={items}
            pagination={false}
            scroll={{ x: sumWidth(draftColumns) }}
          />
          <Typography.Text type="secondary">
            实收 + 拒收不得超过应收数量；拒收需填写异常原因，签收后申领单将置为已关闭。
          </Typography.Text>
        </Space>
      </Modal>

      <Modal
        title="评价配送服务"
        open={evalOpen}
        onCancel={() => setEvalOpen(false)}
        okText="提交评价"
        confirmLoading={evaluate.isPending}
        destroyOnHidden
        onOk={async () => {
          const values = await evalForm.validateFields();
          evaluate.mutate(values);
        }}
      >
        <Form form={evalForm} layout="vertical" preserve={false} initialValues={{ scoreTotal: 5, anonFlag: false }}>
          <Form.Item name="scoreTotal" label="总体评分" rules={[{ required: true, message: '请选择评分' }]}>
            <Rate allowHalf />
          </Form.Item>
          <Form.Item name="tags" label="评价标签">
            <Select mode="tags" placeholder="可多选，也可直接输入" options={EVAL_TAGS} />
          </Form.Item>
          <Form.Item name="content" label="评价内容">
            <Input.TextArea rows={3} maxLength={1000} showCount placeholder="配送时效、包装完好度、服务态度等（可不填）" />
          </Form.Item>
          <Form.Item name="anonFlag" label="匿名评价" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
